import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUser,
  faUsers,
  faComment,
  faGlobe,
  faCalendar,
  faImage,
  faCog,
  faCode,
  faStar,
  faSave,
} from '@fortawesome/free-solid-svg-icons';

const CreateProject = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    projectName: '',
    about: '',
    websiteLink: '',
    startDate: '',
    endDate: '',
    state: 'En développement',
    serviceType: 'Développement Web',
    imageFile: null,
    clientId: '',
    collaboratorIds: [],
    technologyIds: [],
    comment: '',
    rating: '1',
  });

  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [technologies, setTechnologies] = useState([]);

  useEffect(() => {
    axios.get('/client').then(res => setClients(res.data));
    axios.get('/collaborator').then(res => setCollaborators(res.data));
    axios.get('/technology').then(res => setTechnologies(res.data));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => {
      const list = prev[name] || [];
      if (checked) {
        return { ...prev, [name]: [...list, value] };
      } else {
        return { ...prev, [name]: list.filter(item => item !== value) };
      }
    });
  };

  const onCancel = () => {
    navigate('/admin/projects');
  };

  const handleFileChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData(prev => ({ ...prev, imageFile: e.target.files[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const client = clients.find(c => c.id === Number(formData.clientId));
    if (!client) {
      alert('Client invalide');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Token manquant, veuillez vous reconnecter.');
        return;
      }

      const data = new FormData();
      data.append('projectName', formData.projectName);
      data.append('about', formData.about);
      data.append('websiteLink', formData.websiteLink);
      data.append('startDate', formData.startDate);
      data.append('endDate', formData.endDate);
      data.append('state', formData.state);
      data.append('serviceType', formData.serviceType);
      data.append('clientEmail', client.email);

      if (formData.imageFile) {
        data.append('imageUrl', formData.imageFile);
      }

      formData.collaboratorIds.forEach(id => data.append('collaboratorIds', id));
      formData.technologyIds.forEach(id => data.append('technologyIds', id));

      await axios.post('/project', data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const feedbackPayload = {
        clientEmail: client.email,
        projectName: formData.projectName,
        comment: formData.comment,
        rating: Number(formData.rating),
      };

      if (formData.comment.trim()) {
        await axios.post('/feedback', feedbackPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      alert('Projet créé avec succès !');
      navigate('/admin/projects');
    } catch (err) {
      console.error('Erreur lors de la création :', err.response?.data || err.message);
      const message = err.response?.data?.message || 'Erreur lors de la création du projet.';
      alert(Array.isArray(message) ? message.join(', ') : message);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button className="back-button" onClick={onCancel} type="button">
          <FontAwesomeIcon icon={faArrowLeft} />
          Retour
        </button>
        <h2>Créer un nouveau projet</h2>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit} className="project-form">
          <div className="form-section">
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faUser} className="label-icon" />
                Nom du projet
              </label>
              <input
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                placeholder="Entrez le nom du projet"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faComment} className="label-icon" />
                À propos
              </label>
              <textarea
                name="about"
                value={formData.about}
                onChange={handleChange}
                placeholder="Description du projet"
                className="form-textarea"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faGlobe} className="label-icon" />
                Lien du site (facultatif)
              </label>
              <input
                name="websiteLink"
                value={formData.websiteLink}
                onChange={handleChange}
                placeholder="https://exemple.com"
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCalendar} className="label-icon" />
                  Date de début
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCalendar} className="label-icon" />
                  Date de fin
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faImage} className="label-icon" />
                Image du projet
              </label>
              <input
                type="file"
                name="imageFile"
                accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                onChange={handleFileChange}
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCog} className="label-icon" />
                  État
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="En développement">En développement</option>
                  <option value="Déployé">Déployé</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCog} className="label-icon" />
                  Type de service
                </label>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="Développement Web">Développement Web</option>
                  <option value="Développement Mobile">Développement Mobile</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faUser} className="label-icon" />
                Client
              </label>
              <select
                name="clientId"
                value={formData.clientId}
                onChange={handleChange}
                required
                className="form-select"
              >
                <option value="">-- Sélectionner un client --</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faUsers} className="label-icon" />
                Collaborateurs
              </label>
              <div className="checkbox-group">
                {collaborators.map((col) => (
                  <label key={col.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="collaboratorIds"
                      value={col.id}
                      checked={formData.collaboratorIds.includes(col.id.toString())}
                      onChange={handleCheckboxChange}
                    />
                    {col.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faCode} className="label-icon" />
                Technologies
              </label>
              <div className="checkbox-group">
                {technologies.map((tech) => (
                  <label key={tech.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="technologyIds"
                      value={tech.id}
                      checked={formData.technologyIds.includes(tech.id.toString())}
                      onChange={handleCheckboxChange}
                    />
                    {tech.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faComment} className="label-icon" />
                Avis du client (facultatif)
              </label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                placeholder="Avis du client"
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faStar} className="label-icon" />
                Note de l’avis
              </label>
              <select
                name="rating"
                value={formData.rating}
                onChange={handleChange}
                className="form-select"
              >
                <option value="1">⭐ 1</option>
                <option value="2">⭐⭐ 2</option>
                <option value="3">⭐⭐⭐ 3</option>
                <option value="4">⭐⭐⭐⭐ 4</option>
                <option value="5">⭐⭐⭐⭐⭐ 5</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-save">
              <FontAwesomeIcon icon={faSave} />
              Enregistrer
            </button>
            <button className="cancel-button" onClick={onCancel} type="button">
              <FontAwesomeIcon icon={faArrowLeft} />
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProject;
